import { FormEvent, KeyboardEvent, useMemo, useRef, useState } from "react";
import type { Audience, Channel, DesiredTone, EmojiPreference, Mode } from "../types/api";
import type { Preferences } from "../hooks/useLocalPreferences";
import { executeTurnstile } from "../hooks/useTurnstile";
import { SegmentedControl } from "./SegmentedControl";
import { ContextFields } from "./ContextFields";

type CheckFormProps = {
  preferences: Preferences;
  setPreferences: (next: Preferences | ((current: Preferences) => Preferences)) => void;
  siteKey?: string;
  disabled?: boolean;
  onSubmit: (
    request: {
      input: string;
      mode: Mode;
      channel: Channel;
      audience: Audience;
      desired_tone: DesiredTone;
      emoji_preference: EmojiPreference;
    },
    getTurnstileToken: () => Promise<string>
  ) => void;
};

const behaviorExamples = [
  "I say “back in my day” during team meetings",
  "I wear earplugs to concerts",
  "I print directions before a road trip"
];

const communicationExamples = [
  "Ok.",
  "Per my last email…",
  "we’re so back fr fr no cap — a company LinkedIn post"
];

const behaviorPlaceholder = "e.g. I print every concert ticket and keep it in a labeled folder";
const communicationPlaceholder = "Paste the text, email, post, caption, or message you might send…";

function codePointLength(value: string) {
  return Array.from(value).length;
}

function validationMessage(input: string) {
  const trimmed = input.trim();
  if (!trimmed) return "Drop the move first. The vibe department needs evidence.";
  if (codePointLength(trimmed) < 3) return "A few more words, counselor.";
  if (codePointLength(input) > 4000) return "This has become a memoir. Keep it under 4,000 characters.";
  return "";
}

export function CheckForm({ preferences, setPreferences, siteKey, disabled, onSubmit }: CheckFormProps) {
  const [input, setInput] = useState("");
  const [channel, setChannel] = useState<Channel>("unspecified");
  const [audience, setAudience] = useState<Audience>("unspecified");
  const turnstileRef = useRef<HTMLDivElement>(null);
  const mode = preferences.mode;
  const length = codePointLength(input);
  const message = useMemo(() => validationMessage(input), [input]);
  const valid = !message;
  const examples = mode === "behavior" ? behaviorExamples : communicationExamples;
  function updateMode(nextMode: Mode) {
    setPreferences((current) => ({ ...current, mode: nextMode }));
    setChannel("unspecified");
    setAudience("unspecified");
  }

  function handleSubmit(event?: FormEvent) {
    event?.preventDefault();
    if (!valid || disabled) return;
    onSubmit(
      {
        input,
        mode,
        channel: mode === "communication" ? channel : "unspecified",
        audience,
        desired_tone: mode === "communication" ? preferences.desiredTone : "keep_voice",
        emoji_preference: mode === "communication" ? preferences.emojiPreference : "auto"
      },
      () => executeTurnstile(siteKey, turnstileRef.current)
    );
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
      handleSubmit();
    }
  }

  return (
    <form className="check-form" onSubmit={handleSubmit}>
      <SegmentedControl value={mode} onChange={updateMode} disabled={disabled} />
      <label className="textarea-label" htmlFor="unc-input">
        <span>{mode === "behavior" ? "The move" : "The draft"}</span>
      </label>
      <textarea
        id="unc-input"
        value={input}
        disabled={disabled}
        placeholder={mode === "behavior" ? behaviorPlaceholder : communicationPlaceholder}
        rows={5}
        maxLength={4400}
        onKeyDown={handleKeyDown}
        onChange={(event) => setInput(event.target.value)}
        aria-describedby="validation-message char-count"
      />
      <div className="form-meta">
        <p id="char-count" className={length > 4000 ? "danger" : ""} aria-live="polite">
          {length > 3200 ? `${length}/4,000` : ""}
        </p>
      </div>
      <p id="validation-message" className={message && input ? "validation-message" : "sr-only"} aria-live="polite">
        {input ? message : ""}
      </p>
      <ContextFields
        mode={mode}
        open={preferences.contextOpen}
        onToggle={() => setPreferences((current) => ({ ...current, contextOpen: !current.contextOpen }))}
        channel={channel}
        audience={audience}
        desiredTone={preferences.desiredTone}
        emojiPreference={preferences.emojiPreference}
        disabled={disabled}
        onChannelChange={setChannel}
        onAudienceChange={setAudience}
        onDesiredToneChange={(desiredTone) => setPreferences((current) => ({ ...current, desiredTone }))}
        onEmojiPreferenceChange={(emojiPreference) => setPreferences((current) => ({ ...current, emojiPreference }))}
      />
      <div ref={turnstileRef} className="turnstile-slot" aria-hidden="true" />
      <button type="submit" className="primary-button" disabled={!valid || disabled}>
        Check my aura ✨
      </button>
      <div className="example-row" aria-label="Examples">
        {examples.map((example) => (
          <button key={example} type="button" className="example-chip" disabled={disabled} onClick={() => setInput(example)}>
            {example}
          </button>
        ))}
      </div>
    </form>
  );
}
